import { RcsParser, getChildrenFromMask } from './RcsParser.js';
import { createRcsIO } from './RcsIO.js';
import { calculateLevelsToProcess } from './RcsLodUtils.js';

/** @import {SvoPayloadResult} from './RcsParser.js' */
/** @import {OctreeNode} from './RcsFileLoader.js' */

/**
 * Some information about the RCS file format is available on this wiki page:
 * https://autodesk.atlassian.net/wiki/spaces/aeceng/pages/350480523/ReCap+File+Format
 */

/** Verbose logs for debugging */
const VERBOSE = false;

/** RCS on-disk file header size in bytes. */
const RCS_HEADER_BYTES = 98605;

/**
 * Parsed RCS file header.
 * @typedef {Object} RcsHeader
 * @property {string}    magic - File magic string ("RCS")
 * @property {number}    majorVersion
 * @property {number}    minorVersion
 * @property {number[]}  translation - [x, y, z]
 * @property {number[]}  rotation - [x, y, z]
 * @property {number[]}  scale - [x, y, z]
 * @property {number[]}  scannerOrigin - [x, y, z]
 * @property {number[]}  svoBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}  scanBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number}    octreeFlags
 * @property {number}    provider
 * @property {number}    totalPoints
 * @property {boolean}   hasRGB
 * @property {boolean}   hasNormals
 * @property {boolean}   hasIntensity
 * @property {string}    scanId
 * @property {string}    coordinateSystem
 * @property {number}    numFileChunks
 * @property {number}    chunkDirectoryOffset
 */

/**
 * RCS file chunk directory entry.
 * @typedef {Object} RcsChunk
 * @property {number} chunkId - Chunk type ID (1 = voxel data, 2 = umbrella octree, 3 = umbrella extended)
 * @property {number} fileOffsetStart - Byte offset in file
 * @property {number} sizeInBytes
 * @property {string} name - Human-readable chunk name
 */

/**
 * Umbrella octree leaf node — one per scan region.
 * @typedef {Object} UmbrellaLeafNode
 * @property {number}   index - Leaf index within the umbrella array
 * @property {number[]} svoBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]} nodeBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number}   maxDepth - Maximum octree depth for this leaf
 * @property {number}   amountOfPoints - Total point count
 * @property {number}   fileSize - Size of the SVO payload in the file
 * @property {number[]} numNonLeafNodes - Interior node counts per level (length 32)
 * @property {number[]} numSvoLeafNodes - SVO leaf point counts per level (length 32)
 */

/**
 * Result of extracting and converting SVO points for a single node.
 * @typedef {Object} SvoExtractionResult
 * @property {number} nodeIndex
 * @property {SvoPayloadResult & { dataBuffer: Uint8Array }} svoData
 * @property {{ positions: Float32Array, colors: Float32Array|Uint8Array, count: number }} worldPoints
 * @property {Int32Array|null} integerPositions
 * @property {Int32Array|null} normalIndex
 * @property {Int8Array|null}  additionalData
 * @property {{ offsetX: number, offsetY: number, offsetZ: number, scale: number }|null} positionTransform
 */

/**
 * Core RCS Extractor that works in both Node.js and browsers
 * Coordinates I/O operations with parsing logic
 */
export class RcsExtractorCore {
  /**
   * @param {string|null} source - URL of the RCS file or null for the VS production proxy where the resource is identified by URN rather than URL
   * @param {string|null} urn - URN identifying the model resource
   * @param {string|null} token - Bearer token for authentication
   */
  constructor(source, urn, token) {
    this.source = source;
    this.urn = urn;
    this.token = token;

    this.io = createRcsIO(source, urn, token);

    /** @type {RcsHeader|null} */
    this.header = null;
    /** @type {RcsChunk[]} */
    this.chunks = [];
    /** @type {UmbrellaLeafNode[]|null} */
    this.umbrellaOctreeData = null;
    /** @type {{ intermediateNodes: any[], nodeLevelStartIndices: number[], levelNodeCounts: number[] }|null} */
    this.umbrellaExtendedData = null;
  }

  async open() {
    await this.io.open();
  }

  async close() {
    await this.io.close();
  }

  /**
   * Read and parse the header and chunk directory
   * @returns {Promise<{ header: RcsHeader, chunks: RcsChunk[] }>}
   */
  async readHeaderAndChunkDirectory() {
    // Read the header (includes chunk directory)
    const headerData = await this.io.readRange(0, RCS_HEADER_BYTES);

    this.header = RcsParser.parseHeader(headerData);
    this.chunks = RcsParser.parseChunkDirectory(
      headerData,
      this.header.chunkDirectoryOffset,
      this.header.numFileChunks
    );

    return { header: this.header, chunks: this.chunks };
  }
  /**
   * Read umbrella octree data (Chunk 2) - cached for efficiency
   * @returns {Promise<UmbrellaLeafNode[]>}
   */
  async readUmbrellaOctreeData() {
    if (this.umbrellaOctreeData) {
      return this.umbrellaOctreeData;
    }

    const chunk = this.chunks.find((c) => c.chunkId === 2);
    if (!chunk) {
      throw new Error('Umbrella octree data chunk (ID=2) not found');
    }

    const chunkData = await this.io.readRange(Number(chunk.fileOffsetStart), Number(chunk.sizeInBytes));
    this.umbrellaOctreeData = RcsParser.parseUmbrellaOctreeData(chunkData);
    return this.umbrellaOctreeData;
  }

  /**
   * Read umbrella extended data (Chunk 3) - cached for efficiency
   */
  async readUmbrellaExtendedData() {
    if (this.umbrellaExtendedData) {
      return this.umbrellaExtendedData;
    }

    const chunk = this.chunks.find((c) => c.chunkId === 3);
    if (!chunk) {
      throw new Error('Umbrella extended data chunk (ID=3) not found');
    }

    const chunkData = await this.io.readRange(Number(chunk.fileOffsetStart), Number(chunk.sizeInBytes));
    this.umbrellaExtendedData = RcsParser.parseUmbrellaExtendedData(chunkData);
    return this.umbrellaExtendedData;
  }

  /**
   * Read SVO payload data for a specific umbrella leaf node
   * @param {number} leafNodeIndex
   * @returns {Promise<SvoPayloadResult & { dataBuffer: Uint8Array }>}
   */
  async readSvoPayload(leafNodeIndex) {
    // Get umbrella data (cached after first call)
    const umbrellaOctreeData = await this.readUmbrellaOctreeData();

    if (leafNodeIndex < 0 || leafNodeIndex >= umbrellaOctreeData.length) {
      throw new Error(`Invalid leaf node index: ${leafNodeIndex}. Available range: 0-${umbrellaOctreeData.length - 1}`);
    }

    const leafNode = umbrellaOctreeData[leafNodeIndex];
    if (VERBOSE) {
      console.log(`\n=== Reading SVO Payload for Leaf Node ${leafNodeIndex} ===`);
      console.log(`Leaf bounds: [${leafNode.nodeBounds.join(', ')}]`);
      console.log(`Points: ${leafNode.amountOfPoints}, File size: ${leafNode.fileSize} bytes`);
    }

    // Calculate the offset to this leaf's data in the voxel chunk
    const voxelChunk = this.chunks.find((c) => c.chunkId === 1);
    if (!voxelChunk) {
      throw new Error('Voxel data chunk (ID=1) not found');
    }

    // Calculate cumulative offset for this leaf
    let leafDataOffset = Number(voxelChunk.fileOffsetStart);
    for (let i = 0; i < leafNodeIndex; i++) {
      leafDataOffset += umbrellaOctreeData[i].fileSize;
    }

    if (VERBOSE) {
      console.log(`Reading SVO data at offset: ${leafDataOffset}, size: ${leafNode.fileSize}`);
    }

    // Read the SVO payload for this leaf
    const buffer = await this.io.readRange(leafDataOffset, leafNode.fileSize);

    const parsed = RcsParser.parseSvoPayload(buffer, leafNode);
    const dataBuffer = buffer.subarray((parsed.numInteriorNodes + parsed.numSvoLeafNodeCount) * 4, leafNode.fileSize);
    return /** @type {SvoPayloadResult & { dataBuffer: Uint8Array }} */{ ...parsed, dataBuffer };
  }

  /**
   * Read raw SVO payload data for a leaf node (for worker processing)
   * @param {number} leafNodeIndex
   * @param {number} [startPoint=0]
   * @param {number} [endPoint=Infinity]
   * @returns {Promise<Uint8Array>}
   */
  async readRawSvoPayload(leafNodeIndex, startPoint = 0, endPoint = Infinity) {
    // Check cache first
    if (this.umbrellaOctreeData === null) {
      this.umbrellaOctreeData = await this.readUmbrellaOctreeData();
    }

    const leafNode = this.umbrellaOctreeData[leafNodeIndex];

    // Calculate how much data we actually need and where to start reading
    const { offset, size } = this._calculateRequiredDataSize(leafNode, startPoint, endPoint);

    // Calculate the offset to this leaf's data in the voxel chunk
    const voxelChunk = this.chunks.find((c) => c.chunkId === 1);
    if (!voxelChunk) {
      throw new Error('Voxel data chunk (ID=1) not found');
    }

    // Calculate cumulative offset for this leaf's start
    let leafDataStartOffset = Number(voxelChunk.fileOffsetStart);
    for (let i = 0; i < leafNodeIndex; i++) {
      leafDataStartOffset += this.umbrellaOctreeData[i].fileSize;
    }

    // Add the offset within this leaf's data to skip levels we don't need
    const finalOffset = leafDataStartOffset + offset;

    // Read only the necessary portion of the SVO payload data (network optimization)
    const buffer = await this.io.readRange(finalOffset, size);

    // Return as transferable Uint8Array for worker
    return new Uint8Array(buffer);
  }

  /**
   * Calculate the required data offset and size for SVO payload based on LOD range
   * Uses the same algorithm as the decoder to determine exact byte requirements
   * @param {UmbrellaLeafNode} leafNode - The leaf node containing level information
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {{ offset: number, size: number, points?: number }}
   */
  _calculateRequiredDataSize(leafNode, startPoint, endPoint) {
    // Size for interior nodes (if we need any)
    const numInteriorNodes = leafNode.numNonLeafNodes.reduce((sum, count) => sum + count, 0);
    const numSvoLeafNodeTotal = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    const interiorNodeDataSize = (numInteriorNodes + numSvoLeafNodeTotal) * 4;

    // If loading everything, use full size starting from after interior node data
    if (startPoint === 0 && endPoint === Infinity) {
      return {
        offset: interiorNodeDataSize,
        size: leafNode.fileSize - interiorNodeDataSize
      };
    }

    // Calculate which levels to process.
    // Note: interprets startPoint/endPoint as level indices in direct mode and point indices in threshold mode.
    const levelInfo = this._calculateLevelsToProcessForSize(leafNode, startPoint, endPoint);

    // If no levels to process, return early with size 0
    if (levelInfo.levelsToProcess.length === 0) {
      return {
        offset: interiorNodeDataSize,
        size: 0 // Read nothing
      };
    }

    // Calculate offset for the leaf nodes we need
    const { leafStart, leafEnd } = levelInfo.nodeRanges;

    // Check if we're trying to start beyond available data
    // This can happen when the leaf node doesn't have enough points to reach startPoint
    const totalLeafNodes = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    if (leafStart >= totalLeafNodes) {
      // No data to load - return safe values (start after interior nodes with 0 size)
      return {
        offset: interiorNodeDataSize,
        size: 0 // Read nothing
      };
    }

    // Clamp leafEnd to totalLeafNodes to avoid reading beyond available data
    const clampedLeafEnd = Math.min(leafEnd, totalLeafNodes);

    // When interiorNodesIncluded = false, the parser expects the buffer to start
    // at the beginning of leaf data and will skip to leafStart itself.
    // So we read from the start of leaf data (offset = interiorNodeDataSize)
    // and include all leaves from 0 to leafEnd
    const dataOffset = interiorNodeDataSize;

    // Calculate size to include all leaves from 0 to leafEnd
    // The parser will skip the first leafStart leaves itself
    let dataSize;
    if (endPoint === Infinity) {
      // Read all remaining leaf data
      dataSize = leafNode.fileSize - dataOffset;
    } else {
      // Read from start of leaf data to leafEnd
      dataSize = clampedLeafEnd * 16;
    }

    // Don't exceed original size
    const maxSize = leafNode.fileSize - dataOffset;
    return {
      offset: dataOffset,
      size: Math.max(0, Math.min(dataSize, maxSize)),
      points: clampedLeafEnd
    };
  }

  /**
   * Calculate levels to process for size estimation based on point or level range
   * @param {UmbrellaLeafNode} leafNode
   * @param {number} startPointOrLevel - Start of range (point index in threshold mode, level index in direct mode)
   * @param {number} endPointOrLevel - End of range (point index in threshold mode, level index in direct mode)
   * @private
   */
  _calculateLevelsToProcessForSize(leafNode, startPointOrLevel, endPointOrLevel) {
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;

    // Use shared utility to calculate levels to process
    const { levelsToProcess, pointsBeforeStart, totalPoints } =
    calculateLevelsToProcess(numSvoLeafNodes, startPointOrLevel, endPointOrLevel);

    // Calculate node ranges for size estimation
    const nodeRanges = this._calculateNodeRangesForSize(numSvoLeafNodes, levelsToProcess);

    return {
      levelsToProcess,
      nodeRanges,
      actualStartPoint: pointsBeforeStart,
      actualEndPoint: pointsBeforeStart + totalPoints
    };
  }

  /**
   * Calculate node ranges for size estimation (simplified version of parser logic)
   * @param {number[]} numSvoLeafNodes
   * @param {number[]} levelsToProcess
   * @private
   */
  _calculateNodeRangesForSize(numSvoLeafNodes, levelsToProcess) {
    if (levelsToProcess.length === 0) {
      return { leafStart: 0, leafEnd: 0 };
    }

    const minLevel = Math.min(...levelsToProcess);
    const maxLevel = Math.max(...levelsToProcess);

    // Calculate leaf node ranges
    let leafStart = 0;
    let leafEnd = 0;

    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) {
        leafStart += numSvoLeafNodes[level] || 0;
      }
      if (level <= maxLevel) {
        leafEnd += numSvoLeafNodes[level] || 0;
      }
    }

    return { leafStart, leafEnd };
  }

  /**
   * Convert quantized SVO points to local coordinates with interleaved buffer
   * @param {SvoPayloadResult & { dataBuffer: Uint8Array }} svoData
   */
  convertSvoPointsToLocal(svoData) {
    return RcsParser.convertSvoPointsToLocal(svoData);
  }

  /**
   * Read SVO payloads for multiple leaf nodes
   * @param {number[]} nodeIndices - Array of node indices to read
   * @param {{ preserveIntegerPositions?: boolean }} [options]
   * @returns {Promise<SvoExtractionResult[]>}
   */
  async readMultipleSvoPayloads(nodeIndices, options = {}) {
    console.log(`\nReading ${nodeIndices.length} SVO payloads...`);
    /** @type {SvoExtractionResult[]} */
    const results = [];

    for (const nodeIndex of nodeIndices) {
      console.log(`\nProcessing node ${nodeIndex}...`);
      try {
        const svoData = await this.readSvoPayload(nodeIndex);
        const localPoints = this.convertSvoPointsToLocal(svoData);

        const positions = new Float32Array(localPoints.count * 3);

        const preserve = !!options.preserveIntegerPositions;
        const integerPositions = preserve ? new Int32Array(localPoints.count * 3) : null;
        const normalIndex = preserve ? new Int32Array(localPoints.count) : null;
        const additionalData = preserve ? new Int8Array(localPoints.count * 2) : null;
        const colors = preserve ? new Uint8Array(localPoints.count * 4) : new Float32Array(localPoints.count * 3);

        for (let i = 0; i < localPoints.count; i++) {
          const interleavedIndex = i * 6;
          const posIndex = i * 3;
          const colorIndex = i * 4;

          positions[posIndex] = localPoints.interleavedData[interleavedIndex] + localPoints.transformData.offsetX;
          positions[posIndex + 1] = localPoints.interleavedData[interleavedIndex + 1] + localPoints.transformData.offsetY;
          positions[posIndex + 2] = localPoints.interleavedData[interleavedIndex + 2] + localPoints.transformData.offsetZ;

          if (integerPositions) {
            integerPositions[posIndex] = svoData.leafPoints[i].x;
            integerPositions[posIndex + 1] = svoData.leafPoints[i].y;
            integerPositions[posIndex + 2] = svoData.leafPoints[i].z;

            normalIndex[i] = svoData.leafPoints[i].normalIndex;
            additionalData[i * 2] = svoData.leafPoints[i].lidarClass;
            additionalData[i * 2 + 1] = svoData.leafPoints[i].deleteFlag || svoData.leafPoints[i].primitiveClass << 1;

            colors[colorIndex] = svoData.leafPoints[i].r;
            colors[colorIndex + 1] = svoData.leafPoints[i].g;
            colors[colorIndex + 2] = svoData.leafPoints[i].b;
            colors[colorIndex + 3] = svoData.leafPoints[i].a;

          } else {
            colors[posIndex] = localPoints.interleavedData[interleavedIndex + 3];
            colors[posIndex + 1] = localPoints.interleavedData[interleavedIndex + 4];
            colors[posIndex + 2] = localPoints.interleavedData[interleavedIndex + 5];
          }
        }

        /** @type {SvoExtractionResult} */
        const result = {
          nodeIndex,
          svoData,
          worldPoints: { positions, colors, count: localPoints.count },
          integerPositions: integerPositions,
          normalIndex: normalIndex,
          additionalData: additionalData,
          positionTransform: integerPositions ? {
            offsetX: localPoints.transformData.offsetX,
            offsetY: localPoints.transformData.offsetY,
            offsetZ: localPoints.transformData.offsetZ,
            scale: 0.001
          } : null
        };

        results.push(result);

        console.log(`  ✓ Node ${nodeIndex}: ${result.worldPoints.count} points`);
      } catch (error) {
        console.warn(`  ✗ Node ${nodeIndex}: ${error.message}`);
        // Continue with other nodes even if one fails
      }
    }

    console.log(`\nSuccessfully processed ${results.length}/${nodeIndices.length} nodes`);
    return results;
  }

  /**
   * Build hierarchical octree structure from umbrella data
   * @param {UmbrellaLeafNode[]} leafNodes
   * @param {{ intermediateNodes: any[], nodeLevelStartIndices: number[] }} extendedData
   * @returns {{ nodes: OctreeNode[] }}
   */
  buildOctreeStructure(leafNodes, extendedData) {
    const { intermediateNodes, nodeLevelStartIndices } = extendedData;
    /** @type {OctreeNode[]} */
    const nodes = [];
    let nextNodeId = 0;
    const visitedNodes = new Set();

    // Non-recursive function to build the tree structure using a queue
    function buildTreeIteratively() {
      if (intermediateNodes.length === 0) {
        return;
      }

      const queue = [{ nodeIndexInFile: 0, nodeId: nextNodeId++, nodeLevel: 0 }];
      const nodeMap = new Map();

      while (queue.length > 0) {
        const { nodeIndexInFile, nodeId, nodeLevel } = queue.shift();

        // Safety check
        if (nodeIndexInFile < 0 || nodeIndexInFile >= intermediateNodes.length) {
          continue;
        }

        if (visitedNodes.has(nodeIndexInFile)) {
          console.warn(`Circular reference detected at intermediate node ${nodeIndexInFile}`);
          continue;
        }
        visitedNodes.add(nodeIndexInFile);

        const intermediate = intermediateNodes[nodeIndexInFile];
        nodeMap.set(nodeIndexInFile, nodeId);

        const leafIndex = intermediate.indexIntoLeafArray;
        const leafNode = leafIndex >= 0 && leafIndex < leafNodes.length ? leafNodes[leafIndex] : null;

        /** @type {OctreeNode} */
        let node;
        if (leafNode) {
          // Leaf node: owns SVO data and has no children.
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children: [],
            isLeaf: true,
            fileOffsets: {
              svoDataSize: leafNode.fileSize,
              leafIndex: leafIndex
            },
            numSvoLeafNodes: leafNode.numSvoLeafNodes,
            numNonLeafNodes: leafNode.numNonLeafNodes,
            fragmentId: leafIndex,
            nodeBounds: leafNode.nodeBounds
          };
        } else {
          // Interior node: queue children. (A node referencing a leaf index with
          // no leaf data lands here as a childless interior node, as before.)
          const children = [];
          if (!(leafIndex >= 0 && leafIndex < leafNodes.length)) {
            const childMask = getChildrenFromMask(intermediate.childInfo);
            if (childMask.length > 0 && intermediate.firstChildIndex >= 0) {
              for (let i = 0; i < childMask.length; i++) {
                const childIndex = nodeLevelStartIndices[nodeLevel + 1] + intermediate.firstChildIndex + i;
                if (childIndex >= 0 && childIndex < intermediateNodes.length) {
                  const childNodeId = nextNodeId++;
                  children.push(childNodeId);
                  queue.push({
                    nodeIndexInFile: childIndex,
                    nodeId: childNodeId,
                    nodeLevel: nodeLevel + 1
                  });
                }
              }
            }
          }
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children,
            isLeaf: false,
            fileOffsets: {}
          };
        }

        nodes.push(node);
      }
    }

    buildTreeIteratively();

    // Sort nodes by ID to ensure root is first
    nodes.sort((a, b) => a.id - b.id);

    console.log(`Built tree with ${nodes.length} nodes, visited ${visitedNodes.size} intermediate nodes`);

    return { nodes };
  }

  /**
   * Generate JSON output for the octree structure
   */
  async generateJSON() {
    console.log('\n=== Generating Octree JSON ===\n');

    // Read the umbrella octree data
    const leafNodes = await this.readUmbrellaOctreeData();
    const extendedData = await this.readUmbrellaExtendedData();

    // Build the hierarchical structure
    const octreeStructure = this.buildOctreeStructure(leafNodes, extendedData);

    // Add SVO offset information
    const voxelChunk = this.chunks.find((c) => c.chunkId === 1);
    if (voxelChunk) {
      let currentOffset = Number(voxelChunk.fileOffsetStart);

      // Calculate offsets for each leaf based on cumulative file sizes
      octreeStructure.nodes.forEach((node) => {
        if (node.fileOffsets && typeof node.fileOffsets.leafIndex === 'number') {
          const leafIndex = node.fileOffsets.leafIndex;

          // Calculate cumulative offset by summing file sizes of preceding leaves
          let leafOffset = currentOffset;
          for (let i = 0; i < leafIndex; i++) {
            if (leafNodes[i]) {
              leafOffset += leafNodes[i].fileSize;
            }
          }

          node.fileOffsets.svoDataOffset = leafOffset;
          delete node.fileOffsets.leafIndex; // Remove temporary field
        }
      });
    }

    return octreeStructure;
  }

  /**
   * Display header and chunk information in human-readable format
   */
  displayInfo() {
    if (!this.header) {
      console.log('No header information available');
      return;
    }

    console.log('=== RCS File Information ===\n');

    console.log('Header:');
    console.log(`  Magic: ${this.header.magic}`);
    console.log(`  Version: ${this.header.majorVersion}.${this.header.minorVersion}`);
    console.log(`  Total Points: ${this.header.totalPoints.toString()}`);
    console.log(`  Has RGB: ${this.header.hasRGB}`);
    console.log(`  Has Normals: ${this.header.hasNormals}`);
    console.log(`  Has Intensity: ${this.header.hasIntensity}`);
    console.log(`  Scan ID: ${this.header.scanId}`);
    console.log(`  Coordinate System: ${this.header.coordinateSystem}`);

    console.log('\n  Transform:');
    console.log(`    Translation: [${this.header.translation.join(', ')}]`);
    console.log(`    Rotation: [${this.header.rotation.join(', ')}]`);
    console.log(`    Scale: [${this.header.scale.join(', ')}]`);
    console.log(`    Scanner Origin: [${this.header.scannerOrigin.join(', ')}]`);

    console.log('\n  Bounds:');
    console.log(`    SVO Bounds: [${this.header.svoBounds.join(', ')}]`);
    console.log(`    Scan Bounds: [${this.header.scanBounds.join(', ')}]`);

    console.log(`\n  Octree Flags (0x${this.header.octreeFlags.toString(16)}):`);
    const flags = RcsParser.getOctreeFlags(this.header.octreeFlags);
    flags.forEach((flag) => console.log(`    - ${flag}`));

    console.log('\n=== Chunk Directory ===\n');
    console.log(`Total Chunks: ${this.header.numFileChunks}\n`);

    this.chunks.forEach((chunk, index) => {
      console.log(`Chunk ${index + 1}:`);
      console.log(`  ID: ${chunk.chunkId}`);
      console.log(`  Name: ${chunk.name}`);
      console.log(`  Offset: ${chunk.fileOffsetStart.toString()}`);
      console.log(`  Size: ${chunk.sizeInBytes.toString()} bytes`);
      console.log('');
    });
  }
}

// Export functions for standalone use
export { getChildrenFromMask };